import React, { useEffect, useState, useCallback } from "react";
import styles from "./FiscalDeviceInfoScreen.module.scss";
import classNames from "classnames";
import { EditIcon, DeleteIcon } from "../../../../assets";
import { useDispatch, useSelector } from "react-redux";
import {
  deleteFiscalDevice,
  deleteFiscalDeviceResetState,
  fetchSigngleFiscalDevice,
  getFiscalDeviceInvoices,
  updateFiscalDeviceResetState,
} from "../../../../store/actions/admin/fiscalDevice";
import { useHistory } from "react-router-dom";
import moment from "moment";
import UpdateFiscalDeviceModal from "../../../../components/modals/updateFiscalDeviceModal/UpdateFiscalDeviceModal";
import { toast } from "react-toastify";
import { Link } from "react-router-dom";
import AreYouSureModal from "../../../../components/modals/areYouSureModal/AreYouSureModal";
import ResourceList from "../../../../components/resourceList/ResourceList";
import { isMobile } from "react-device-detect";
import DoubleInvoicesModal from "../../../../components/modals/doubleInvoicesModal/DoubleInvoicesModal";
import tagsService from "../../../../apis/services/admin/tagsService";
import fiscalDeviceService from "../../../../apis/services/admin/fiscalDeviceService";
import SecondaryButton from "../../../../components/secondaryButton/SecondaryButton";
import Checkbox from "../../../../components/checkbox/Checkbox";
import BulkReassignVendingControllersModal from "../../../../components/modals/bulkReassignVendingControllersModal/BulkReassignVendingControllersModal";
import BulkReleaseVendingControllersModal from "../../../../components/modals/bulkReleaseVendingControllersModal/BulkReleaseVendingControllersModal";
import StickerPrintModal from "../../../../components/modals/stickerPrintModal/StickerPrintModal";
import { Switch } from "@material-ui/core";

// Helper: get contrast color (black/white) based on background
const getContrastColor = (hex) => {
  if (!hex) return "#000";
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return (r * 299 + g * 587 + b * 114) / 1000 >= 128 ? "#000" : "#fff";
};

// Smart Card Health helper functions
const formatDuration = (ms) => {
  if (!ms) return "-";
  if (ms < 1000) return `${ms}ms`;
  if (ms < 60000) return `${(ms / 1000).toFixed(1)}s`;
  if (ms < 3600000) return `${Math.floor(ms / 60000)}min ${Math.floor((ms % 60000) / 1000)}s`;
  return `${Math.floor(ms / 3600000)}h ${Math.floor((ms % 3600000) / 60000)}min`;
};

const translateErrorType = (type) => {
  const translations = {
    'NONE': 'Nema greške',
    'PCSC_SERVICE_ERROR': 'Greška PC/SC servisa',
    'CARD_COMMUNICATION_ERROR': 'Greška komunikacije sa karticom',
    'CARD_NOT_TRANSACTED': 'Kartica zauzeta',
    'CARD_INVALID_HANDLE': 'Nevažeći handle kartice',
    'PKCS11_INIT_ERROR': 'Greška inicijalizacije PKCS11',
    'PIN_ERROR': 'Greška PIN-a',
    'CERTIFICATE_ERROR': 'Greška sertifikata',
    'PHYSICAL_CARD_ERROR': 'Potrebna fizička intervencija',
    'UNKNOWN_ERROR': 'Nepoznata greška',
  };
  return translations[type || ''] || type || '-';
};

const translateRecoveryAction = (action) => {
  const translations = {
    'NONE': 'Bez akcije',
    'PCSC_RESTART': 'Restart PC/SC servisa',
    'CARD_RECONNECT': 'Ponovno povezivanje kartice',
    'PROTOCOL_RESET': 'Reset protokola',
    'PKCS11_REINIT': 'Reinicijalizacija PKCS11',
    'PIN_REVERIFY': 'Ponovna verifikacija PIN-a',
    'FULL_RESET': 'Potpuni reset',
    'PHYSICAL_INTERVENTION_REQUIRED': 'Potrebna fizička intervencija',
  };
  return translations[action || ''] || action || '-';
};

const getHealthStatus = (health) => {
  if (!health) return { status: 'unknown', color: 'gray', label: 'Nema podataka' };
  
  if (health.physicalInterventionRequired) {
    return { status: 'critical', color: 'red', label: 'Potrebna intervencija!' };
  }
  
  if (health.currentErrorType && health.currentErrorType !== 'NONE') {
    return { status: 'error', color: 'red', label: 'Greška' };
  }
  
  const uptime = parseFloat(health.uptimePercentage);
  if (uptime && uptime < 95) {
    return { status: 'warning', color: 'yellow', label: 'Upozorenje' };
  }
  
  return { status: 'healthy', color: 'green', label: 'Zdravo' };
};

const HEADER_VALUES = [
  { label: "BROJ RAČUNA", key: "invoiceNumber", sortable: true },
  { label: "VREME NA PFR SERVERU", key: "sdcDateTime", sortable: true },
  { label: "UKUPAN IZNOS", key: "totalAmount", sortable: true },
  { label: "UKUPAN IZNOS POREZA", key: "taxTotalAmount", sortable: false },
  { label: "RAČUN" },
  // { label: "MAK ADRESA", key: "macAddress", sortable: true },
  // { label: "ONLINE", key: "lastOnlineAt", sortable: true },
];

const VENDING_CONTROLLERS_HEADER = [
  { label: "IME", key: "invoiceNumber", sortable: false },
  { label: "SERIJSKI BROJ", key: "serialNumber", sortable: false },
  { label: "VERZIJA", key: "version", sortable: false },
  { label: "MAK ADRESA", key: "invoiceNumber", sortable: false },
  { label: "ONLINE", key: "lastOnlineAt", sortable: false },
  {
    label: "LINK ZA PREUZIMANJE RAČUNA",
    key: "invoiceNumber",
    sortable: false,
  },
  { label: "NALEPNICA", key: "sticker", sortable: false },
];

const FiscalDeviceInfoScreen = ({ id }) => {
  const dispatch = useDispatch();
  const [updateFiscalDeviceModalVisible, setUpdateFiscalDeviceModalVisible] =
    useState(false);
  const [deleteFiscalDeviceModalVisible, setDeleteFiscalDeviceModalVisible] =
    useState(false);
  const [selectedVendingControllers, setSelectedVendingControllers] = useState([]);
  const [bulkReassignModalOpen, setBulkReassignModalOpen] = useState(false);
  const [bulkReleaseModalOpen, setBulkReleaseModalOpen] = useState(false);
  const [stickerModalMode, setStickerModalMode] = useState(null); // null | "print" | "email"
  const [stickerModalControllers, setStickerModalControllers] = useState([]);
  const [failoverConfirmOpen, setFailoverConfirmOpen] = useState(false);
  const [failoverSaving, setFailoverSaving] = useState(false);

  const [doubleInvoicesModalOpen, setDoubleInvoicesModalOpen] = useState(false);
  const fiscalDevice = useSelector(
    (state) => state.fiscalDevices.singleFiscalDevice
  );
  const updateFiscalDeviceInfo = useSelector(
    (state) => state.fiscalDevices.updateFiscalDeviceInfo
  );
  const deleteFiscalDeviceInfo = useSelector(
    (state) => state.fiscalDevices.deleteFiscalDeviceInfo
  );
  const invoices = useSelector(
    (state) => state.fiscalDevices.fiscalDeviceInvoices
  );

  const [copiedItemText, setCopiedItemText] = useState(null);
  
  // Tags state
  const [allTags, setAllTags] = useState([]);
  const [deviceTags, setDeviceTags] = useState([]);
  const [tagsLoading, setTagsLoading] = useState(false);
  const [showTagSelector, setShowTagSelector] = useState(false);

  const history = useHistory();
  const vendingControllers = fiscalDevice?.data?.vendingControllers || [];
  const failover = fiscalDevice?.data?.failover;
  const isVirtualDevice =
    !!fiscalDevice?.data && !fiscalDevice?.data?.salesDataController;

  const isVendingControllerSelected = (controller) =>
    selectedVendingControllers.some((selected) => selected.id === controller.id);

  const toggleVendingControllerSelection = (controller) =>
    setSelectedVendingControllers((prev) =>
      prev.some((selected) => selected.id === controller.id)
        ? prev.filter((selected) => selected.id !== controller.id)
        : [...prev, controller]
    );

  const allVendingControllersSelected =
    vendingControllers.length > 0 &&
    vendingControllers.every(isVendingControllerSelected);

  const toggleAllVendingControllers = () =>
    setSelectedVendingControllers(
      allVendingControllersSelected ? [] : vendingControllers
    );

  const onBulkReassignSuccess = () => {
    setSelectedVendingControllers([]);
    setBulkReassignModalOpen(false);
    dispatch(fetchSigngleFiscalDevice(id));
  };

  const onBulkReleaseSuccess = () => {
    setSelectedVendingControllers([]);
    setBulkReleaseModalOpen(false);
    dispatch(fetchSigngleFiscalDevice(id));
  };

  const openStickerModal = (mode, controllers) => {
    setStickerModalControllers(controllers);
    setStickerModalMode(mode);
  };

  // Štampa/slanje ne menja ništa u SDF bazi — nema refetch-a
  const onStickerSuccess = () => {
    setSelectedVendingControllers([]);
    setStickerModalMode(null);
  };

  const onConfirmFailoverToggle = async () => {
    if (failoverSaving) return;
    setFailoverSaving(true);
    try {
      await fiscalDeviceService.toggleFailover(id, !failover?.active);
      toast.info(failover?.active ? "Failover isključen" : "Failover uključen");
      dispatch(fetchSigngleFiscalDevice(id));
    } catch (err) {
      toast.error(
        err?.response?.data?.message ||
          err?.message ||
          "Greška pri promeni failover stanja"
      );
    } finally {
      setFailoverSaving(false);
      setFailoverConfirmOpen(false);
    }
  };

  const renderFailoverStatus = () => {
    if (failover?.active) {
      return (
        <p>
          Aktivno — {failover.movedCount} ESP uređaja prebačeno na{" "}
          <Link to={`/fiscal-devices/${failover.virtualPfr?.id}`}>
            {failover.virtualPfr?.name}
          </Link>
        </p>
      );
    }
    if (failover?.virtualPfrAmbiguous) {
      return <p>Više virtuelnih PFR-ova u firmi — izaberite ručno</p>;
    }
    if (!failover?.virtualPfr) {
      return <p>Virtuelni PFR nije pronađen u firmi</p>;
    }
    if (failover?.certificateStatus === "missing") {
      return <p>Nema sertifikata</p>;
    }
    if (failover?.certificateStatus === "expired") {
      return <p>Sertifikat istekao</p>;
    }
    if (failover?.certificateStatus === "not_yet_valid") {
      return <p>Sertifikat još nije važeći</p>;
    }
    if (failover?.certificateStatus === "unknown") {
      return <p>Validnost sertifikata nepoznata</p>;
    }
    if (!failover?.eligibleCount) {
      return <p>Nema ESP uređaja za prebacivanje</p>;
    }
    return (
      <p>
        Neaktivno — {failover.eligibleCount} ESP uređaja će biti prebačeno na
        "{failover.virtualPfr?.name}"
      </p>
    );
  };

  // Fetch all available tags
  const fetchAllTags = useCallback(async () => {
    try {
      const response = await tagsService.fetchTags();
      setAllTags(response?.data || []);
    } catch (err) {
      console.error("Error fetching tags:", err);
    }
  }, []);

  // Update device tags from fetched device data
  useEffect(() => {
    if (fiscalDevice?.data?.tags) {
      setDeviceTags(fiscalDevice.data.tags);
    }
  }, [fiscalDevice?.data?.tags]);

  // Fetch all tags on mount
  useEffect(() => {
    fetchAllTags();
  }, [fetchAllTags]);

  // Handle adding/removing tags
  const handleToggleTag = async (tagId) => {
    setTagsLoading(true);
    try {
      const isTagged = deviceTags.some(t => t.id === tagId);
      if (isTagged) {
        await tagsService.removeTagFromDevice(id, tagId);
        setDeviceTags(deviceTags.filter(t => t.id !== tagId));
        toast.success("Tag uklonjen");
      } else {
        await tagsService.addTagToDevice(id, tagId);
        const tag = allTags.find(t => t.id === tagId);
        if (tag) {
          setDeviceTags([...deviceTags, tag]);
        }
        toast.success("Tag dodat");
      }
    } catch (err) {
      console.error("Error toggling tag:", err);
      toast.error("Greška pri ažuriranju tagova");
    } finally {
      setTagsLoading(false);
    }
  };

  useEffect(() => {
    if (id) {
      getFiscalDeviceInvoices(id, 1, 15, "");
    }
  }, [id, dispatch]);

  const onLoadPage = (page) => {
    dispatch(
      getFiscalDeviceInvoices(id, page, invoices.perPage, searchTerm, "", "")
    );
  };
  const [searchTerm, setSearchTerm] = useState("");

  const onSort = (sortBy, order) => {
    dispatch(
      getFiscalDeviceInvoices(
        id,
        invoices.page,
        invoices.perPage,
        searchTerm,
        sortBy,
        order
      )
    );
  };

  useEffect(() => {
    if (copiedItemText) {
      setTimeout(() => {
        setCopiedItemText(null);
      }, 600);
    }
  }, [copiedItemText]);

  useEffect(() => {
    if (updateFiscalDeviceInfo.success) {
      toast.info("Sacuvano");
      setUpdateFiscalDeviceModalVisible(false);
      dispatch(updateFiscalDeviceResetState());
    }
  }, [updateFiscalDeviceInfo.success]);

  useEffect(() => {
    if (updateFiscalDeviceInfo.error) {
      toast.error("Greska");
    }
  }, [updateFiscalDeviceInfo.error]);

  useEffect(() => {
    if (deleteFiscalDeviceInfo.success) {
      toast.info("Obrisano");
      setUpdateFiscalDeviceModalVisible(false);
      dispatch(deleteFiscalDeviceResetState());
      history.goBack();
    }
  }, [deleteFiscalDeviceInfo.success]);

  useEffect(() => {
    if (deleteFiscalDeviceInfo.error) {
      toast.error("Greska");
    }
  }, [deleteFiscalDeviceInfo.error]);

  const getCommandType = (commandType) => {
    switch (commandType) {
      case "SYNC_MANAGED_VENDING_CONTROLLERS":
        return "Sinhorinizacija vending kontrolera";
      case "SYNC_POS_NUMBER":
        return "Sinhronizacija ESIR broja";
      case "SET_TRAINING_MODE":
        return "Postavi obuku";
      case "REMOVE_TRAINING_MODE":
        return "Ukini obuku";
      case "UPDATE_VENDING_PREFERENCES":
        return "Azuriraj postavke za vending";
      case "SYNC_MISSING_INVOICES":
        return "Sinhronizacija računa";
      case "SEND_PENDING_SDF_INVOICES":
        return "Pošalji račune na SDF (SDF računi na čekanju)";
      case "SEND_ALL_AUDIT_PACKAGES":
        return "Pošalji audit pakete (Pending audit paketi)";
      default:
        return null;
    }
  };

  const isoToLocal = (date) => {
    let tzoffset = new Date().getTimezoneOffset() * 60000; //offset in milliseconds
    let localISOTime = new Date(date.getTime() - tzoffset)
      .toISOString()
      .slice(0, -1);
    return localISOTime;
  };

  const renderCommands = () => {
    return (
      <>
        <div className={styles.vendingControllersHeader}>
          <div className={styles.headerItem}>
            <p>Vrsta</p>
          </div>
          <div className={styles.headerItem}>
            <p>Datum kreiranja</p>
          </div>
        </div>
        <div className={styles.cardContent}>
          {fiscalDevice?.data?.fiscalDeviceCommands?.map((command, index) => {
            return (
              <div
                className={classNames(
                  styles.itemRow,
                  styles.borderTop,
                  index ===
                  fiscalDevice?.data?.fiscalDeviceCommands.length - 1 &&
                  styles.borderBottom
                )}
              >
                <div className={styles.itemValue}>
                  <p>{getCommandType(command.type)}</p>
                </div>
                <div className={styles.itemValue}>
                  <p>{isoToLocal(new Date(command.createdAt))}</p>
                </div>
              </div>
            );
          })}
        </div>
      </>
    );
  };

  const renderSmartCardHealth = () => {
    const health = fiscalDevice?.data?.smartCardHealth;
    
    if (!health) {
      return (
        <div className={styles.cardContent}>
          <p className={styles.noDataMessage}>
            Podaci o zdravlju smart kartice nisu dostupni za ovaj uređaj.
            Ova funkcionalnost je dostupna samo za novije verzije softvera.
          </p>
        </div>
      );
    }

    const healthStatus = getHealthStatus(health);
    const lastSuccessfulOp = health.lastSuccessfulOperationTimestamp
      ? moment(health.lastSuccessfulOperationTimestamp)
        .tz(moment.tz.guess())
        .format("DD-MM-YYYY HH:mm:ss")
      : "-";
    const pcscErrorTime = health.pcscErrorTimestamp
      ? moment(health.pcscErrorTimestamp)
        .tz(moment.tz.guess())
        .format("DD-MM-YYYY HH:mm:ss")
      : "-";

    return (
      <div className={styles.cardContent}>
        {/* Health Status Badge */}
        <div className={styles.configRow}>
          <div className={styles.configLabel}>
            <p className={styles.textBold}>Status:</p>
          </div>
          <div className={styles.configValue}>
            <div
              className={classNames(
                healthStatus.color === 'green' ? styles.online :
                healthStatus.color === 'yellow' ? styles.warning :
                styles.ofline
              )}
            />
            <p>{healthStatus.label}</p>
          </div>
        </div>

        {/* Physical Intervention Alert */}
        {health.physicalInterventionRequired && (
          <div className={classNames(styles.configRow, styles.alertRow)}>
            <div className={styles.alertContent}>
              <p className={styles.alertText}>
                ⚠️ Potrebna fizička intervencija: {health.physicalInterventionReason || 'Nepoznat razlog'}
              </p>
            </div>
          </div>
        )}

        {/* Current Error Type */}
        <div className={styles.configRow}>
          <div className={styles.configLabel}>
            <p className={styles.textBold}>Tip greške:</p>
          </div>
          <div className={styles.configValue}>
            <p>{translateErrorType(health.currentErrorType)}</p>
          </div>
        </div>

        {/* Error Message */}
        {health.currentErrorMessage && (
          <div className={styles.configRow}>
            <div className={styles.configLabel}>
              <p className={styles.textBold}>Poruka greške:</p>
            </div>
            <div className={styles.configValue}>
              <p>{health.currentErrorMessage}</p>
            </div>
          </div>
        )}

        {/* Uptime */}
        <div className={styles.configRow}>
          <div className={styles.configLabel}>
            <p className={styles.textBold}>Uptime:</p>
          </div>
          <div className={styles.configValue}>
            <div className={styles.uptimeBar}>
              <div 
                className={styles.uptimeProgress} 
                style={{ width: `${parseFloat(health.uptimePercentage) || 0}%` }}
              />
            </div>
            <p className={styles.uptimeText}>{health.uptimePercentage != null ? parseFloat(health.uptimePercentage).toFixed(1) : '-'}%</p>
          </div>
        </div>

        {/* Last Successful Operation */}
        <div className={styles.configRow}>
          <div className={styles.configLabel}>
            <p className={styles.textBold}>Poslednja uspešna operacija:</p>
          </div>
          <div className={styles.configValue}>
            <p>{lastSuccessfulOp}</p>
          </div>
        </div>

        {/* Error Statistics */}
        <div className={styles.configRow}>
          <div className={styles.configLabel}>
            <p className={styles.textBold}>Uzastopne greške:</p>
          </div>
          <div className={styles.configValue}>
            <p>{health.consecutiveErrorCount ?? '-'}</p>
          </div>
        </div>

        <div className={styles.configRow}>
          <div className={styles.configLabel}>
            <p className={styles.textBold}>Ukupno grešaka:</p>
          </div>
          <div className={styles.configValue}>
            <p>{health.totalErrorCountSinceStartup ?? '-'}</p>
          </div>
        </div>

        {health.errorDurationMs && (
          <div className={styles.configRow}>
            <div className={styles.configLabel}>
              <p className={styles.textBold}>Trajanje greške:</p>
            </div>
            <div className={styles.configValue}>
              <p>{formatDuration(health.errorDurationMs)}</p>
            </div>
          </div>
        )}

        <div className={styles.configRow}>
          <div className={styles.configLabel}>
            <p className={styles.textBold}>Najduže trajanje greške:</p>
          </div>
          <div className={styles.configValue}>
            <p>{formatDuration(health.longestErrorDurationMs)}</p>
          </div>
        </div>

        {/* Recovery Statistics */}
        <div className={styles.configRow}>
          <div className={styles.configLabel}>
            <p className={styles.textBold}>Poslednja akcija oporavka:</p>
          </div>
          <div className={styles.configValue}>
            <p>{translateRecoveryAction(health.lastRecoveryAction)}</p>
          </div>
        </div>

        <div className={styles.configRow}>
          <div className={styles.configLabel}>
            <p className={styles.textBold}>Poslednji oporavak uspešan:</p>
          </div>
          <div className={styles.configValue}>
            <div
              className={classNames(
                health.lastRecoverySuccessful ? styles.online : styles.ofline
              )}
            />
            <p>{health.lastRecoverySuccessful ? 'Da' : 'Ne'}</p>
          </div>
        </div>

        <div className={styles.configRow}>
          <div className={styles.configLabel}>
            <p className={styles.textBold}>Uspešni oporavci:</p>
          </div>
          <div className={styles.configValue}>
            <p>{health.successfulRecoveryCount ?? '-'}</p>
          </div>
        </div>

        <div className={styles.configRow}>
          <div className={styles.configLabel}>
            <p className={styles.textBold}>Neuspešni oporavci:</p>
          </div>
          <div className={styles.configValue}>
            <p>{health.failedRecoveryCount ?? '-'}</p>
          </div>
        </div>

        {/* Recovery Action Counts */}
        <div className={styles.configRow}>
          <div className={styles.configLabel}>
            <p className={styles.textBold}>PC/SC restartova:</p>
          </div>
          <div className={styles.configValue}>
            <p>{health.pcscRestartCount ?? '-'}</p>
          </div>
        </div>

        <div className={styles.configRow}>
          <div className={styles.configLabel}>
            <p className={styles.textBold}>Reconnect kartice:</p>
          </div>
          <div className={styles.configValue}>
            <p>{health.cardReconnectCount ?? '-'}</p>
          </div>
        </div>

        <div className={styles.configRow}>
          <div className={styles.configLabel}>
            <p className={styles.textBold}>Reset protokola:</p>
          </div>
          <div className={styles.configValue}>
            <p>{health.protocolResetCount ?? '-'}</p>
          </div>
        </div>

        <div className={styles.configRow}>
          <div className={styles.configLabel}>
            <p className={styles.textBold}>PKCS11 reinit:</p>
          </div>
          <div className={styles.configValue}>
            <p>{health.pkcs11ReinitCount ?? '-'}</p>
          </div>
        </div>

        {/* PC/SC Status */}
        <div className={styles.configRow}>
          <div className={styles.configLabel}>
            <p className={styles.textBold}>PC/SC greška:</p>
          </div>
          <div className={styles.configValue}>
            <div
              className={classNames(
                health.hasPcscError ? styles.ofline : styles.online
              )}
            />
            <p>{health.hasPcscError ? 'Da' : 'Ne'}</p>
          </div>
        </div>

        {health.hasPcscError && health.pcscError && (
          <>
            <div className={styles.configRow}>
              <div className={styles.configLabel}>
                <p className={styles.textBold}>PC/SC greška:</p>
              </div>
              <div className={styles.configValue}>
                <p>{health.pcscError}</p>
              </div>
            </div>
            <div className={styles.configRow}>
              <div className={styles.configLabel}>
                <p className={styles.textBold}>Vreme PC/SC greške:</p>
              </div>
              <div className={styles.configValue}>
                <p>{pcscErrorTime}</p>
              </div>
            </div>
          </>
        )}
      </div>
    );
  };

  const renderAdditionalInfo = () => {
    if (fiscalDevice?.data) {
      const lastOnline = fiscalDevice.data?.lastOnlineAt;
      const lastTimeOnline = lastOnline
        ? moment(lastOnline).tz(moment.tz.guess()).format("DD-MM-YYYY HH:mm:ss")
        : "";

      const isVirtualPFR = !fiscalDevice?.data?.salesDataController;
      const isOnline = isVirtualPFR
        ? true
        : new Date().getTime() - fiscalDevice?.data.lastOnlineAt > 300 * 1000
          ? false
          : true;

      // Format certificate dates
      const certValidFrom = fiscalDevice.data?.cardCertificateValidFrom
        ? moment(fiscalDevice.data.cardCertificateValidFrom)
          .tz(moment.tz.guess())
          .format("DD-MM-YYYY")
        : "-";

      const certValidTo = fiscalDevice.data?.cardCertificateValidTo
        ? moment(fiscalDevice.data.cardCertificateValidTo)
          .tz(moment.tz.guess())
          .format("DD-MM-YYYY")
        : "-";

      const lastAttentionTime = fiscalDevice.data?.lastAttentionTime
        ? moment(fiscalDevice.data.lastAttentionTime)
          .tz(moment.tz.guess())
          .format("DD-MM-YYYY HH:mm:ss")
        : "-";

      const lastInvoiceAttempt = fiscalDevice.data?.lastInvoiceAttempt
        ? moment(fiscalDevice.data.lastInvoiceAttempt)
          .tz(moment.tz.guess())
          .format("DD-MM-YYYY HH:mm:ss")
        : "-";

      const address = fiscalDevice?.data?.businessUnit?.address;
      const formattedAddress = address 
        ? `${address.name || ''}, ${address.locality || ''}, ${address.district || ''}, ${address.countryCode || ''}`
        : "-";
      return (
        <>
          <div className={styles.configRow}>
            <div className={styles.configLabel}>
              <p className={styles.textBold}>Ime:</p>
            </div>
            <div className={classNames(styles.configValue)}>
              <p>{fiscalDevice?.data?.name}</p>
            </div>
          </div>
          <div className={styles.configRow}>
            <div className={styles.configLabel}>
              <p className={styles.textBold}>Mac adresa:</p>
            </div>
            <div className={styles.configValue}>
              <p>{fiscalDevice?.data?.macAddress}</p>
            </div>
          </div>
          <div className={styles.configRow}>
            <div className={styles.configLabel}>
              <p className={styles.textBold}>Firma:</p>
            </div>
            <div className={styles.configValue}>
              {fiscalDevice?.data?.businessUnit?.company?.id ? (
                <p
                  className={styles.clickableCompany}
                  onClick={() => history.push(`/companies/${fiscalDevice.data.businessUnit.company.id}`)}
                >
                  {fiscalDevice?.data?.businessUnit?.company?.name}
                </p>
              ) : (
                <p>{fiscalDevice?.data?.businessUnit?.company?.name}</p>
              )}
            </div>
          </div>
          <div className={styles.configRow}>
            <div className={styles.configLabel}>
              <p className={styles.textBold}>PIB:</p>
            </div>
            <div className={styles.configValue}>
              <p>{fiscalDevice?.data?.businessUnit?.company?.PIB}</p>
            </div>
          </div>
          <div className={styles.configRow}>
            <div className={styles.configLabel}>
              <p className={styles.textBold}>Poslovni prostor:</p>
            </div>
            <div className={styles.configValue}>
              <p>{formattedAddress}</p>
            </div>
          </div>
          {fiscalDevice?.data?.scUuid && (
            <div className={styles.configRow}>
              <div className={styles.configLabel}>
                <p className={styles.textBold}>Bezbednosni element:</p>
              </div>
              <div className={styles.configValue}>
                <p>{fiscalDevice?.data?.scUuid}</p>
              </div>
            </div>
          )}
          {fiscalDevice?.data?.vsdcKeystorePrincipal && (
            <div className={styles.configRow}>
              <div className={styles.configLabel}>
                <p className={styles.textBold}>Keystore CN:</p>
              </div>
              <div className={styles.configValue}>
                <p>{fiscalDevice?.data?.vsdcKeystorePrincipal}</p>
              </div>
            </div>
          )}
          {fiscalDevice?.data?.cardCertificateValidFrom && (
            <div className={styles.configRow}>
              <div className={styles.configLabel}>
                <p className={styles.textBold}>Sertifikat važi od:</p>
              </div>
              <div className={styles.configValue}>
                <p>{certValidFrom}</p>
              </div>
            </div>
          )}
          {fiscalDevice?.data?.cardCertificateValidTo && (
            <div className={styles.configRow}>
              <div className={styles.configLabel}>
                <p className={styles.textBold}>Sertifikat važi do:</p>
              </div>
              <div className={styles.configValue}>
                <p>{certValidTo}</p>
              </div>
            </div>
          )}
          {fiscalDevice.data?.lastAttentionTime && (
            <div className={styles.configRow}>
              <div className={styles.configLabel}>
                <p className={styles.textBold}>Last attention:</p>
              </div>
              <div className={styles.configValue}>
                <p>{lastAttentionTime}</p>
              </div>
            </div>
          )}
          {fiscalDevice?.data?.lastInvoiceAttempt && (
            <div className={styles.configRow}>
              <div className={styles.configLabel}>
                <p className={styles.textBold}>Last invoice attempt:</p>
              </div>
              <div className={styles.configValue}>
                <p>{lastInvoiceAttempt}</p>
              </div>
            </div>
          )}
          {fiscalDevice?.data?.version && (
            <div className={styles.configRow}>
              <div className={styles.configLabel}>
                <p className={styles.textBold}>Verzija:</p>
              </div>
              <div className={styles.configValue}>
                <p>{fiscalDevice?.data?.version}</p>
              </div>
            </div>
          )}
          <div className={styles.configRow}>
            <div className={styles.configLabel}>
              <p className={styles.textBold}>Zadnji put viđen</p>
            </div>
            <div className={styles.configValue}>
              <div
                className={classNames(isOnline ? styles.online : styles.ofline)}
              />
              <p>{lastTimeOnline}</p>
            </div>
          </div>
          <div className={styles.configRow}>
            <div className={styles.configLabel}>
              <p className={styles.textBold}>Javna adresa:</p>
            </div>
            <div className={styles.configValue}>
              <p>{fiscalDevice?.data?.publicIpAddress}</p>
            </div>
          </div>
          <div className={styles.configRow}>
            <div className={styles.configLabel}>
              <p className={styles.textBold}>Status čitača:</p>
            </div>
            <div className={styles.configValue}>
              <div
                className={classNames(
                  isVirtualPFR || fiscalDevice?.data?.readerConnected
                    ? styles.online
                    : styles.ofline
                )}
              />
              <p>
                {isVirtualPFR
                  ? "V-PFR"
                  : fiscalDevice?.data?.readerConnected
                    ? "Čitač povezan"
                    : "Čitač nije povezan"}
              </p>
            </div>
          </div>
          <div className={styles.configRow}>
            <div className={styles.configLabel}>
              <p className={styles.textBold}>Status kartice:</p>
            </div>
            <div className={styles.configValue}>
              <div
                className={classNames(
                  isVirtualPFR || fiscalDevice?.data?.cardConnected
                    ? styles.online
                    : styles.ofline
                )}
              />
              <p>
                {isVirtualPFR
                  ? "V-PFR"
                  : fiscalDevice?.data?.cardConnected
                    ? "Kartica povezana"
                    : "Kartica nije povezana"}
              </p>
            </div>
          </div>
          <div className={styles.configRow}>
            <div className={styles.configLabel}>
              <p className={styles.textBold}>Status PIN-a:</p>
            </div>
            <div className={styles.configValue}>
              <div
                className={classNames(
                  isVirtualPFR || fiscalDevice?.data?.pinOk
                    ? styles.online
                    : styles.ofline
                )}
              />
              <p>
                {isVirtualPFR
                  ? "V-PFR"
                  : fiscalDevice?.data?.pinOk
                    ? "PIN verifikovan"
                    : "PIN nije verifikovan"}
              </p>
            </div>
          </div>
          <div className={styles.configRow}>
            <div className={styles.configLabel}>
              <p className={styles.textBold}>Interni SDC URL:</p>
            </div>
            <div className={styles.configValue}>
              <p>{fiscalDevice?.data?.deviceEsdcUrl}</p>
            </div>
          </div>
          <div className={styles.configRow}>
            <div className={styles.configLabel}>
              <p className={styles.textBold}>Interni pin:</p>
            </div>
            <div className={styles.configValue}>
              <p>{fiscalDevice?.data?.internalPin}</p>
            </div>
          </div>
          <div className={styles.configRow}>
            <div className={styles.configLabel}>
              <p className={styles.textBold}>Interni pac:</p>
            </div>
            <div className={styles.configValue}>
              <p>{fiscalDevice?.data?.internalPac}</p>
            </div>
          </div>
          {fiscalDevice?.data?.deviceUseAsVending !== undefined && (
            <div className={styles.configRow}>
              <div className={styles.configLabel}>
                <p className={styles.textBold}>Vending režim:</p>
              </div>
              <div className={styles.configValue}>
                <div
                  className={classNames(
                    fiscalDevice?.data?.deviceUseAsVending
                      ? styles.online
                      : styles.ofline
                  )}
                />
                <p>
                  {fiscalDevice?.data?.deviceUseAsVending
                    ? "Uključen"
                    : "Isključen"}
                </p>
              </div>
            </div>
          )}
          <div className={styles.configRow}>
            <div className={styles.configLabel}>
              <p className={styles.textBold}>Status LPFR:</p>
            </div>
            <div className={styles.configValue}>
              <div
                className={classNames(
                  fiscalDevice?.data?.isInitialized
                    ? styles.online
                    : styles.ofline
                )}
              />
              <p>
                {fiscalDevice?.data?.isInitialized
                  ? "LPFR je konfigurisan"
                  : "LPFR nije konfigurisan"}
              </p>
            </div>
          </div>
          <div className={styles.configRow}>
            <div className={styles.configLabel}>
              <p className={styles.textBold}>Iščitavanje započeto:</p>
            </div>
            <div className={styles.configValue}>
              <p>
                {fiscalDevice?.data?.remoteAuditStartTimestamp
                  ? moment(fiscalDevice?.data?.remoteAuditStartTimestamp)
                    .tz("Europe/Belgrade")
                    .format("DD-MM-YYYY HH:mm:ss")
                  : ""}
              </p>
            </div>
          </div>
          <div className={styles.configRow}>
            <div className={styles.configLabel}>
              <p className={styles.textBold}>Iščitavanje završeno:</p>
            </div>
            <div className={styles.configValue}>
              <p>
                {fiscalDevice?.data?.lastRemoteAuditEndTime
                  ? moment(fiscalDevice?.data?.lastRemoteAuditEndTime)
                    .tz("Europe/Belgrade")
                    .format("DD-MM-YYYY HH:mm:ss")
                  : ""}
              </p>
            </div>
          </div>
          <div className={styles.configRow}>
            <div className={styles.configLabel}>
              <p className={styles.textBold}>Broj audit paketa:</p>
            </div>
            <div className={styles.configValue}>
              <p>{fiscalDevice?.data?.auditFilesCount}</p>
            </div>
          </div>
          <div className={styles.configRow}>
            <div className={styles.configLabel}>
              <p className={styles.textBold}>SDF računi na čekanju:</p>
            </div>
            <div className={styles.configValue}>
              <p>{fiscalDevice?.data?.pendingFSCInvoices?.length}</p>
            </div>
          </div>
          <div className={styles.configRow}>
            <div className={styles.configLabel}>
              <p className={styles.textBold}>Računi na čekanju:</p>
            </div>
            <div className={styles.configValue}>
              <p>{fiscalDevice?.data?.pendingInvoices?.length}</p>
            </div>
          </div>
          <div className={styles.configRow}>
            <div className={styles.configLabel}>
              <p className={styles.textBold}>Nepostojeci računi:</p>
            </div>
            <div className={styles.configValue}>
              <p>
                {fiscalDevice?.data?.missingAuditPackages &&
                  fiscalDevice?.data?.missingAuditPackages.length}
              </p>
            </div>
          </div>
          {fiscalDevice?.data?.localIpAddress && (
            <div className={styles.configRow}>
              <a
                href={`http://${fiscalDevice?.data?.localIpAddress}:5000/connect?ip=${fiscalDevice?.data?.localIpAddress}`}
                target="_blank"
                rel="noopener noreferrer"
                className={styles.aTag}
              >
                <p
                  className={classNames(styles.configLabel, styles.clickable)}
                  onClick={(e) => {
                    e.stopPropagation();
                  }}
                >
                  Lokalna IP adresa: {fiscalDevice?.data?.localIpAddress}
                </p>
              </a>
            </div>
          )}
        </>
      );
    }
  };

  const renderResourceListRow = (invoice, index) => {
    let totalTaxAmount = 0;
    invoice?.taxItems?.forEach((taxItem) => {
      totalTaxAmount += taxItem.amount;
    });

    const localDt = invoice.sdcDateTimeTimestamp
      ? moment(invoice.sdcDateTimeTimestamp)
        .tz(moment.tz.guess())
        .format("HH:mm:ss DD-MM-YYYY")
      : moment
        .utc(invoice.createdAt)
        .tz(moment.tz.guess())
        .format("HH:mm:ss DD-MM-YYYY");

    if (isMobile) {
      return (
        <div className={styles.listCard}>
          <div className={styles.rowCard}>
            <p className={styles.rowCardTitle}>Broj računa</p>
            <p className={styles.rowCardValue}>{invoice.invoiceNumber}</p>
          </div>
          <div className={styles.rowCard}>
            <p className={styles.rowCardTitle}>Vreme na PFR serveru</p>
            <p className={styles.rowCardValue}>{localDt}</p>
          </div>
          <div className={styles.rowCard}>
            <p className={styles.rowCardTitle}>Ukupan iznos</p>
            <p className={styles.rowCardValue}>{invoice.totalAmount}</p>
          </div>
          <div className={styles.rowCard}>
            <p className={styles.rowCardTitle}>Porez</p>
            <p className={styles.rowCardValue}>{totalTaxAmount}</p>
          </div>
          <div className={styles.rowCard}>
            <p className={styles.rowCardTitle}>Zurnal</p>
            <a
              href={invoice.convertedUrlPreview}
              target="_blank"
              className={styles.journalLink}
            >
              Prikaži zurnal
            </a>
          </div>
        </div>
      );
    }
    return (
      <div
        className={classNames(
          styles.row,
          index % 2 === 0 ? styles.even : styles.odd
        )}
        key={invoice.id}
        onClick={() => { }}
      >
        <div className={styles.cell}>
          <p>{invoice.invoiceNumber}</p>
        </div>
        <div className={styles.cell}>
          <p>{localDt}</p>
        </div>
        <div className={styles.cell}>
          <p>{invoice.totalAmount}</p>
        </div>
        <div className={styles.cell}>
          <p>{totalTaxAmount}</p>
        </div>
        <div className={styles.cell}>
          <a
            href={invoice.convertedUrlPreview}
            target="_blank"
            className={styles.journalLink}
          >
            Prikaži zurnal
          </a>
        </div>
      </div>
    );
  };

  const renderVedndingControllerListRow = (item, index) => {
    const isOnline =
      new Date().getTime() - parseInt(item.lastOnlineAt) < 5 * 60 * 1000;
    const lastTimeOnline = item.lastOnlineAt
      ? moment(item.lastOnlineAt)
        .tz(moment.tz.guess())
        .format("DD-MM-YYYY HH:mm:ss")
      : "";

    if (isMobile) {
      return (
        <div className={styles.listCard}>
          <div className={styles.rowCard}>
            <p className={styles.rowCardTitle}>Izaberi</p>
            <Checkbox
              onCheck={() => toggleVendingControllerSelection(item)}
              checked={isVendingControllerSelected(item)}
            />
          </div>
          <div className={styles.rowCard}>
            <p className={styles.rowCardTitle}>Ime</p>
            <p className={styles.rowCardValue}>{item.name}</p>
          </div>
          <div className={styles.rowCard}>
            <p className={styles.rowCardTitle}>Mak adresa</p>
            <Link
              to={"/vending-controllers/" + item.id}
              className={styles.link}
            >
              <p className={styles.rowCardValue}>{item.macAddress}</p>
            </Link>
          </div>
          <div className={styles.rowCard}>
            <p className={styles.rowCardTitle}>Online</p>
            <div className={styles.flexRow}>
              {isOnline ? (
                <div className={styles.online} />
              ) : (
                <div className={styles.ofline} />
              )}
              <p className={styles.rowCardValue}>{lastTimeOnline}</p>
            </div>
          </div>
          <div className={styles.rowCard}>
            <p className={styles.rowCardTitle}>Link za preuzimanje računa</p>
            <p
              className={classNames(styles.rowCardValue, styles.clickable)}
              onClick={() => {
                if (copiedItemText !== item.id) {
                  setCopiedItemText(item.id);
                  navigator.clipboard.writeText(
                    `https://sdf.rs/invoices/validation/${item.macAddress}`
                  );
                }
              }}
            >
              {copiedItemText === item.id ? "Kopirano" : `Kopiraj tekst`}
            </p>
          </div>
          <div className={styles.rowCard}>
            <p className={styles.rowCardTitle}>Nalepnica</p>
            <div className={styles.stickerActions}>
              <p
                className={classNames(styles.rowCardValue, styles.clickable)}
                onClick={() => openStickerModal("print", [item])}
              >
                Štampaj
              </p>
              <p
                className={classNames(styles.rowCardValue, styles.clickable)}
                onClick={() => openStickerModal("email", [item])}
              >
                Email
              </p>
            </div>
          </div>
        </div>
      );
    } else
      return (
        <div
          className={classNames(
            styles.row,
            index % 2 === 0 ? styles.even : styles.odd
          )}
          key={item.id}
          onClick={() => { }}
        >
          <div className={styles.checkBoxWrap}>
            <Checkbox
              onCheck={() => toggleVendingControllerSelection(item)}
              checked={isVendingControllerSelected(item)}
            />
          </div>
          <div className={styles.cell}>
            <p>{item.name}</p>
          </div>
          <div className={styles.cell}>
            <p>{item.serialNumber}</p>
          </div>
          <div className={styles.cell}>
            <p>{item.version}</p>
          </div>
          <div className={styles.cell}>
            <Link
              to={"/vending-controllers/" + item.id}
              className={styles.link}
            >
              <p className={styles.clickable}>{item.macAddress}</p>
            </Link>
          </div>
          <div className={styles.cell}>
            <div className={styles.flexRow}>
              {isOnline ? (
                <div className={styles.online} />
              ) : (
                <div className={styles.ofline} />
              )}
              <p className={styles.rowCardValue}>{lastTimeOnline}</p>
            </div>
          </div>
          <div className={styles.cell}>
            <p
              className={styles.clickable}
              onClick={() => {
                if (copiedItemText !== item.id) {
                  setCopiedItemText(item.id);
                  navigator.clipboard.writeText(
                    `https://sdf.rs/invoices/validation/${item.macAddress}`
                  );
                }
              }}
            >
              {copiedItemText === item.id ? "Kopirano" : "Kopiraj link"}
            </p>
          </div>
          <div className={styles.cell}>
            <div className={styles.stickerActions}>
              <p
                className={styles.clickable}
                onClick={() => openStickerModal("print", [item])}
              >
                Štampaj
              </p>
              <p
                className={styles.clickable}
                onClick={() => openStickerModal("email", [item])}
              >
                Email
              </p>
            </div>
          </div>
        </div>
      );
  };

  return (
    <>
      <UpdateFiscalDeviceModal
        fiscalDevice={fiscalDevice?.data}
        modalOpen={updateFiscalDeviceModalVisible}
        onClose={() => setUpdateFiscalDeviceModalVisible(false)}
        attempt={updateFiscalDeviceInfo.attempt}
      />
      <AreYouSureModal
        onConfirm={() => {
          dispatch(deleteFiscalDevice(fiscalDevice?.data?.id));
        }}
        modalOpen={deleteFiscalDeviceModalVisible}
        closeModal={() => setDeleteFiscalDeviceModalVisible(false)}
      />
      <DoubleInvoicesModal
        modalOpen={doubleInvoicesModalOpen}
        onClose={() => {
          setDoubleInvoicesModalOpen(false);
        }}
        fiscalDeviceId={id}
      />
      <BulkReassignVendingControllersModal
        modalOpen={bulkReassignModalOpen}
        onClose={() => setBulkReassignModalOpen(false)}
        selectedControllers={selectedVendingControllers}
        onSuccess={onBulkReassignSuccess}
      />
      <BulkReleaseVendingControllersModal
        modalOpen={bulkReleaseModalOpen}
        onClose={() => setBulkReleaseModalOpen(false)}
        selectedControllers={selectedVendingControllers}
        onSuccess={onBulkReleaseSuccess}
      />
      <StickerPrintModal
        modalOpen={stickerModalMode !== null}
        mode={stickerModalMode || "print"}
        controllers={stickerModalControllers}
        companyId={fiscalDevice?.data?.businessUnit?.company?.id}
        onClose={() => setStickerModalMode(null)}
        onSuccess={onStickerSuccess}
      />
      <AreYouSureModal
        modalOpen={failoverConfirmOpen}
        closeModal={() => setFailoverConfirmOpen(false)}
        onConfirm={onConfirmFailoverToggle}
        message={
          failover?.active
            ? `Vraćaš ${failover?.movedCount} ESP uređaja sa "${failover?.virtualPfr?.name}" nazad na "${fiscalDevice?.data?.name}". Da li si siguran?`
            : `Prebacuješ ${failover?.eligibleCount} ESP uređaja sa "${fiscalDevice?.data?.name}" na "${failover?.virtualPfr?.name}". Da li si siguran?`
        }
      />
      <div className={styles.content}>
        <div className={classNames(styles.gridHalves)}>
          <div className={classNames(styles.sectionMax, styles.maxHeight)}>
            <div className={styles.sectionHeader}>
              <p className={styles.sectionTitle}>Informacije o uređaju</p>
              <div className={styles.actionsWrap}>
                <img
                  src={DeleteIcon}
                  alt="delete"
                  className={styles.deleteButton}
                  onClick={() => {
                    setDeleteFiscalDeviceModalVisible(true);
                  }}
                />
                <img
                  src={EditIcon}
                  alt="edit"
                  className={styles.editButton}
                  onClick={() => {
                    setUpdateFiscalDeviceModalVisible(true);
                  }}
                />
              </div>
            </div>
            <div className={classNames(styles.card)}>
              <div className={styles.cardContent}>{renderAdditionalInfo()}</div>
            </div>
          </div>
          <div className={styles.gridColumn}>
            <div
              className={classNames(
                styles.sectionMax,
                styles.maxHeight,
                styles.flexGrow
              )}
            >
              <div className={styles.sectionHeader}>
                <p className={styles.sectionTitle}>Komande na čekanju</p>
              </div>
              <div className={classNames(styles.card)}>
                <div className={styles.cardContent}>{renderCommands()}</div>
              </div>
            </div>
            {fiscalDevice?.data && !isVirtualDevice && (
              <div className={classNames(styles.sectionMax, styles.maxHeight)}>
                <div className={styles.sectionHeader}>
                  <p className={styles.sectionTitle}>
                    Failover na virtuelni PFR
                  </p>
                </div>
                <div className={classNames(styles.card)}>
                  <div className={styles.cardContent}>
                    <div className={styles.configRow}>
                      <div className={styles.configLabel}>
                        <p className={styles.textBold}>Uključeno:</p>
                      </div>
                      <div className={styles.configValue}>
                        <Switch
                          color="primary"
                          checked={!!failover?.active}
                          disabled={
                            failoverSaving ||
                            (!failover?.active && !failover?.canEnable)
                          }
                          onChange={() => setFailoverConfirmOpen(true)}
                        />
                      </div>
                    </div>
                    <div className={styles.configRow}>
                      <div className={styles.configLabel}>
                        <p className={styles.textBold}>Status:</p>
                      </div>
                      <div className={styles.configValue}>
                        {renderFailoverStatus()}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
        
        {/* Tags Section */}
        <div className={classNames(styles.section, styles.marginTop)}>
          <div className={styles.sectionHeader}>
            <p className={styles.sectionTitle}>Tagovi</p>
            <button
              className={styles.addTagButton}
              onClick={() => setShowTagSelector(!showTagSelector)}
            >
              {showTagSelector ? "Zatvori" : "+ Dodaj tag"}
            </button>
          </div>
          <div className={classNames(styles.card)}>
            <div className={styles.tagsSection}>
              {/* Current tags */}
              <div className={styles.currentTags}>
                {deviceTags.length === 0 ? (
                  <p className={styles.noTagsText}>Nema dodeljenih tagova</p>
                ) : (
                  deviceTags.map((tag) => (
                    <span
                      key={tag.id}
                      className={styles.tagBadge}
                      style={{
                        backgroundColor: tag.color,
                        color: getContrastColor(tag.color),
                      }}
                    >
                      {tag.name}
                      <button
                        className={styles.removeTagBtn}
                        onClick={() => handleToggleTag(tag.id)}
                        disabled={tagsLoading}
                        style={{ color: getContrastColor(tag.color) }}
                      >
                        ×
                      </button>
                    </span>
                  ))
                )}
              </div>
              
              {/* Tag selector */}
              {showTagSelector && (
                <div className={styles.tagSelector}>
                  <p className={styles.tagSelectorTitle}>Klikni da dodaš/ukloniš tag:</p>
                  <div className={styles.tagOptions}>
                    {allTags.map((tag) => {
                      const isSelected = deviceTags.some(t => t.id === tag.id);
                      return (
                        <div
                          key={tag.id}
                          className={classNames(
                            styles.tagOption,
                            isSelected && styles.tagOptionSelected
                          )}
                          style={{
                            backgroundColor: isSelected ? tag.color : "#f0f0f0",
                            color: isSelected ? getContrastColor(tag.color) : "#333",
                            borderColor: tag.color,
                          }}
                          onClick={() => !tagsLoading && handleToggleTag(tag.id)}
                        >
                          {isSelected && "✓ "}{tag.name}
                        </div>
                      );
                    })}
                  </div>
                  {allTags.length === 0 && (
                    <p className={styles.noTagsAvailable}>
                      Nema dostupnih tagova. <Link to="/tags">Kreiraj tagove</Link>
                    </p>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>

        {fiscalDevice?.data?.vendingControllers &&
          fiscalDevice?.data?.vendingControllers.length !== 0 && (
            <div className={classNames(styles.section, styles.marginTop)}>
              <div className={styles.sectionHeader}>
                <p className={styles.sectionTitle}>Vending kontroleri</p>
              </div>
              {selectedVendingControllers.length > 0 && (
                <div className={styles.selectionBar}>
                  <p>{selectedVendingControllers.length} izabrano</p>
                  <SecondaryButton
                    name="Promeni fiskalni uređaj"
                    onClick={() => setBulkReassignModalOpen(true)}
                  />
                  <SecondaryButton
                    name="Sačuvaj istoriju + oslobodi"
                    onClick={() => setBulkReleaseModalOpen(true)}
                  />
                  <SecondaryButton
                    name="Štampaj nalepnice"
                    onClick={() =>
                      openStickerModal("print", selectedVendingControllers)
                    }
                  />
                  <SecondaryButton
                    name="Nalepnice na email"
                    onClick={() =>
                      openStickerModal("email", selectedVendingControllers)
                    }
                  />
                  <SecondaryButton
                    name="Poništi izbor"
                    onClick={() => setSelectedVendingControllers([])}
                  />
                </div>
              )}
              {fiscalDevice?.data?.vendingControllers && (
                <ResourceList
                  selectable={true}
                  onSelectAll={toggleAllVendingControllers}
                  selectAll={allVendingControllersSelected}
                  resource={{ data: fiscalDevice?.data?.vendingControllers }}
                  onSort={() => { }}
                  onLoadPage={() => { }}
                  headerValues={VENDING_CONTROLLERS_HEADER}
                  rowRender={renderVedndingControllerListRow}
                />
              )}
            </div>
          )}
        {fiscalDevice?.data?.businessUnit?.certificates &&
          fiscalDevice?.data?.businessUnit?.certificates.length !== 0 && (
            <div className={classNames(styles.section, styles.marginTop)}>
              <div className={styles.sectionHeader}>
                <p className={styles.sectionTitle}>Sertifikati za Virutelni</p>
              </div>
              <div className={classNames(styles.card)}>
                <div className={styles.certificatesContent}>
                  {fiscalDevice.data.businessUnit.certificates.map((cert, index) => (
                    <div key={cert.id} className={styles.certificateCard}>
                      <div className={styles.configRow}>
                        <div className={styles.configLabel}>
                          <p className={styles.textBold}>Certificate ID:</p>
                        </div>
                        <div className={styles.configValue}>
                          <p>{cert.certificateId}</p>
                        </div>
                      </div>
                      <div className={styles.configRow}>
                        <div className={styles.configLabel}>
                          <p className={styles.textBold}>PAC:</p>
                        </div>
                        <div className={styles.configValue}>
                          <p>{cert.pac}</p>
                        </div>
                      </div>
                      <div className={styles.configRow}>
                        <div className={styles.configLabel}>
                          <p className={styles.textBold}>Keystore Password:</p>
                        </div>
                        <div className={styles.configValue}>
                          <p>{cert.keystorePassword}</p>
                        </div>
                      </div>
                      <div className={styles.configRow}>
                        <div className={styles.configLabel}>
                          <p className={styles.textBold}>File Name:</p>
                        </div>
                        <div className={styles.configValue}>
                          <p>{cert.fileName}</p>
                        </div>
                      </div>
                      <div className={styles.configRow}>
                        <div className={styles.configLabel}>
                          <p className={styles.textBold}>VSDC URL:</p>
                        </div>
                        <div className={styles.configValue}>
                          <p>{cert.vscdUrl}</p>
                        </div>
                      </div>
                      {cert.validFrom && (
                        <div className={styles.configRow}>
                          <div className={styles.configLabel}>
                            <p className={styles.textBold}>Valid From:</p>
                          </div>
                          <div className={styles.configValue}>
                            <p>{moment(cert.validFrom).format("DD-MM-YYYY")}</p>
                          </div>
                        </div>
                      )}
                      {cert.validTo && (
                        <div className={styles.configRow}>
                          <div className={styles.configLabel}>
                            <p className={styles.textBold}>Valid To:</p>
                          </div>
                          <div className={styles.configValue}>
                            <p>{moment(cert.validTo).format("DD-MM-YYYY")}</p>
                          </div>
                        </div>
                      )}
                      <div className={styles.configRow}>
                        <div className={styles.configLabel}>
                          <p className={styles.textBold}>Created At:</p>
                        </div>
                        <div className={styles.configValue}>
                          <p>{moment(cert.createdAt).format("DD-MM-YYYY HH:mm:ss")}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        {invoices && invoices.length !== 0 && (
          <div className={classNames(styles.section, styles.marginBottom)}>
            <div className={styles.sectionHeader}>
              <p className={styles.sectionTitle}>Računi</p>
            </div>
            <div>
              <ResourceList
                rowRender={renderResourceListRow}
                onSort={onSort}
                onLoadPage={onLoadPage}
                headerValues={HEADER_VALUES}
                resource={invoices}
                minHeight
                showLoader={true}
              />
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default FiscalDeviceInfoScreen;
